import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Upload, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CsvImportPanel = ({ kind, title, hint, onImported }) => {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const downloadTemplate = async () => {
        try {
            const r = await axios.get(`${API}/onboarding/templates/${kind}.csv`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            Object.assign(document.createElement('a'), { href: url, download: `plantilla_${kind}.csv` }).click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error('No se pudo descargar la plantilla');
        }
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusy(true);
        setResult(null);
        try {
            const r = await axios.post(`${API}/onboarding/import/${kind}`, { csv: await file.text() });
            setResult(r.data);
            if (r.data.created) toast.success(`${r.data.created} de ${r.data.total} filas importadas`);
            if (r.data.errors.length) toast.warning(`${r.data.errors.length} filas con errores`);
            onImported?.();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'No se pudo importar el archivo');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="rounded-xl border border-border p-5 space-y-3" data-testid={`csv-panel-${kind}`}>
            <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-xs text-muted-foreground">{hint}</p>
            </div>
            <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid={`csv-template-${kind}`}><Download className="w-4 h-4 mr-1" />Descargar plantilla</Button>
                <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`csv-upload-${kind}`}>
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Importar CSV
                </Button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid={`csv-input-${kind}`} />
            </div>
            {result && (
                <div className="text-sm space-y-1" data-testid={`csv-result-${kind}`}>
                    <p className="flex items-center gap-1 text-emerald-500"><CheckCircle2 className="w-4 h-4" />{result.created} de {result.total} filas importadas</p>
                    {result.errors.slice(0, 8).map((er) => (
                        <p key={er.row} className="flex items-start gap-1 text-xs text-red-400"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Fila {er.row}: {er.error}</p>
                    ))}
                    {result.errors.length > 8 && <p className="text-xs text-muted-foreground">…y {result.errors.length - 8} errores más</p>}
                </div>
            )}
        </div>
    );
};
